/**
 * g18-batch: the plan card. Proposed tasks that share a plan (`viko_task_create_batch`) are one
 * decision: "Plan: <title> — 33 tasks · 6 waves · critical path 20 · 4 deep / 21 standard / 8
 * fast", with waves, each task's tier and reason, its dependencies and the files it shares with
 * other plan tasks. Computed here at the endpoint: E2E titles exist only after decryption.
 */
import type { TaskPlanTag } from '@viko/protocol'
import { buildPlanGraph } from '@viko/scheduler'
import type { TaskDetail } from './types.js'

type Tier = 'fast' | 'standard' | 'deep'

export interface PlanCardTask {
  id: string
  title: string
  ref: string
  tier?: Tier
  modelReason?: string
  /** Plan tasks this one waits for (titles), then existing tasks outside the plan (ids). */
  deps: { id: string; title: string; inPlan: boolean }[]
  files: string[]
  risk?: 'normal' | 'high'
}

export interface PlanCardModel {
  planId: string
  title: string
  doc?: string
  /** Proposed task ids of this plan (what "Approve plan" readies). */
  taskIds: string[]
  waves: PlanCardTask[][]
  criticalPath: number
  tiers: { fast: number; standard: number; deep: number; unset: number }
  /** Plan tasks touching the same files; Viko runs each pair one after the other. */
  conflicts: { a: string; b: string; files: string[] }[]
}

/** Planner tier/reason: E2E records carry them flat; the hub's task rows as `modelChoice`. */
function modelOf(t: TaskDetail): { tier?: Tier; modelReason?: string } {
  const tier = t.tier ?? t.modelChoice?.tier
  const reason = t.modelReason ?? t.modelChoice?.reason
  return { ...(tier ? { tier } : {}), ...(reason ? { modelReason: reason } : {}) }
}

function sharedFiles(a: readonly string[], b: readonly string[]): string[] {
  const out = new Set<string>()
  for (const fa of a)
    for (const fb of b) {
      if (fa === fb) out.add(fa)
      else if (fb.endsWith('/') && fa.startsWith(fb)) out.add(fa)
      else if (fa.endsWith('/') && fb.startsWith(fa)) out.add(fb)
    }
  return [...out]
}

/** One card per plan with proposed tasks, newest plan first. */
export function planCards(tasks: readonly TaskDetail[]): PlanCardModel[] {
  const groups = new Map<string, { tag: TaskPlanTag; tasks: TaskDetail[] }>()
  for (const t of tasks) {
    if (t.status !== 'proposed' || !t.plan) continue
    const g = groups.get(t.plan.id)
    if (g) g.tasks.push(t)
    else groups.set(t.plan.id, { tag: t.plan, tasks: [t] })
  }
  const titleOf = new Map(tasks.map((t) => [t.id, t.title]))
  const cards: (PlanCardModel & { at: number })[] = []
  for (const [planId, g] of groups) {
    const members = [...g.tasks].sort((x, y) => x.createdAt - y.createdAt || (x.id < y.id ? -1 : 1))
    const inPlan = new Set(members.map((t) => t.id))
    const built = buildPlanGraph(
      members.map((t) => ({
        ref: t.id,
        dependsOn: t.deps.filter((d) => inPlan.has(d)),
        ...(t.plan?.files ? { files: t.plan.files } : {}),
        ...(modelOf(t).tier ? { tier: modelOf(t).tier } : {}),
      })),
      { max: Number.MAX_SAFE_INTEGER },
    )
    const byId = new Map(members.map((t) => [t.id, t]))
    const view = (t: TaskDetail): PlanCardTask => ({
      id: t.id,
      title: t.title,
      ref: t.plan?.ref ?? t.id,
      ...modelOf(t),
      deps: t.deps.map((d) => ({ id: d, title: titleOf.get(d) ?? d, inPlan: inPlan.has(d) })),
      files: t.plan?.files ?? [],
      ...(t.plan?.risk ? { risk: t.plan.risk } : {}),
    })
    // A stored plan is acyclic (validated when proposed); fall back to one wave if not.
    const waves = built.ok
      ? built.graph.waves.map((w) => w.map((id) => view(byId.get(id)!)))
      : [members.map(view)]
    const tiers = { fast: 0, standard: 0, deep: 0, unset: 0 }
    for (const t of members) tiers[modelOf(t).tier ?? 'unset']++
    const conflicts: PlanCardModel['conflicts'] = []
    for (let i = 0; i < members.length; i++)
      for (let j = i + 1; j < members.length; j++) {
        const files = sharedFiles(members[i]!.plan?.files ?? [], members[j]!.plan?.files ?? [])
        if (files.length) conflicts.push({ a: members[i]!.title, b: members[j]!.title, files })
      }
    cards.push({
      planId,
      title: g.tag.title,
      ...(g.tag.doc ? { doc: g.tag.doc } : {}),
      taskIds: members.map((t) => t.id),
      waves,
      criticalPath: built.ok ? built.graph.criticalPath.length : members.length,
      tiers,
      conflicts,
      at: Math.max(...members.map((t) => t.createdAt)),
    })
  }
  return cards.sort((x, y) => y.at - x.at).map(({ at: _at, ...c }) => c)
}

const n = (v: number) => v.toLocaleString()
const plural = (v: number, one: string, many = `${one}s`) => `${n(v)} ${v === 1 ? one : many}`

/** "33 tasks · 6 waves · critical path 20 · 4 deep / 21 standard / 8 fast" (numbers in full). */
export function planSummary(c: PlanCardModel): string {
  const tiers = (
    [
      ['deep', c.tiers.deep],
      ['standard', c.tiers.standard],
      ['fast', c.tiers.fast],
      ['no tier', c.tiers.unset],
    ] as const
  )
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${n(v)} ${k}`)
    .join(' / ')
  return [
    plural(c.taskIds.length, 'task'),
    plural(c.waves.length, 'wave'),
    `critical path ${n(c.criticalPath)}`,
    ...(tiers ? [tiers] : []),
  ].join(' · ')
}
